import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNotification } from "../context/NotificationContext";
import { apiService } from "../services/api";
import octopusInstance from "../services/octopus/octopusInstance";
import { getExchangeCode } from "../services/exchanges";
import { fetchProfileDataAction } from "../store/actions/profile";
import { fetchPositionsAction } from "../store/actions/portfolio";
import { getMarginType } from "../utils/utilfunction";
import { closeOrderWindow } from '../store/actions/orders';
import { fetchFundsViewV2 } from "../services/backendAPI";
import { fetchFundsView } from "../store/actions/funds";
import { enqueueSnackbar } from "notistack";
const PRIME_SETTINGS = {

  "NSE": {
    "intraday": {
      "multiplier": 500,
      "absolute": 0
    },
    "overnight": {
      "multiplier": 500,
      "absolute": 0
    }
  },
  "NFO": {
    "FUTURES": {
      "BUY": {
        "intraday": {
          "multiplier": 500,
          "minimumValue": 225000,
          "absolute": 0
        },
        "overnight": {
          "multiplier": 50,
          "minimumValue": 22500,
          "absolute": 0
        }
      },
      "SELL": {
        "intraday": {
          "multiplier": 500,
          "minimumValue": 225000,
          "absolute": 0
        },
        "overnight": {
          "multiplier": 50,
          "minimumValue": 22500,
          "absolute": 0
        }
      }
    },
    "OPTIONS": {
      "BUY": {
        "intraday": {
          "multiplier": 10,
          "minimumValue": 4500,
          "absolute": 0
        },
        "overnight": {
          "multiplier": 2,
          "minimumValue": 900,
          "absolute": 0
        }
      },
      "SELL": {
        "intraday": {
          "multiplier": 0,
          "minimumValue": 225000,
          "absolute": 7500
        },
        "overnight": {
          "multiplier": 0,
          "minimumValue": 50000,
          "absolute": 7500
        }
      }
    }
  },
  "MCX": {
    "FUTURES": {
      "BUY": {
        "intraday": {
          "multiplier": 500,
          "minimumValue": 225000,
          "absolute": 0
        },
        "overnight": {
          "multiplier": 100,
          "minimumValue": 45000,
          "absolute": 0
        }
      },
      "SELL": {
        "intraday": {
          "multiplier": 500,
          "minimumValue": 225000,
          "absolute": 0
        },
        "overnight": {
          "multiplier": 100,
          "minimumValue": 45000,
          "absolute": 0
        }
      }
    },
    "OPTIONS": {
      "BUY": {
        "intraday": {
          "multiplier": 10,
          "minimumValue": 4500,
          "absolute": 0
        },
        "overnight": {
          "multiplier": 2,
          "minimumValue":900,
          "absolute": 0
        }
      },
      "SELL": {
        "intraday": {
          "multiplier": 0,
          "minimumValue": 225000,
          "absolute": 7500
        },
        "overnight": {
          "multiplier": 0,
          "minimumValue": 50000,
          "absolute": 7500
        }
      }
    }
  }
}



export const calcMargin = ({
  order_config,
  exchange,
  orderSide,
  product,
  price,
  qty,
  multiplier,
  strike_price,
  instrumentName = "",
  raw_board_lot_quantity = 1
}) => {
  const marginType = getMarginType(product);
  let marginVal = 0;
  const exchangeConfig = order_config[exchange];
  
  // Handle option sell orders consistently for all exchanges including MCX
  if (orderSide === "SELL" && instrumentName.startsWith("OPT")) {
    let marginMultiplier;
    
    // Get the margin multiplier based on exchange
      if (exchange === "NSE") {
        marginMultiplier = exchangeConfig[orderSide][marginType.toLowerCase()];
        marginVal = marginMultiplier.multiplier;
      } else {
        // NFO, MCX, or other exchanges
        marginMultiplier = exchangeConfig["OPTIONS"][orderSide][marginType.toLowerCase()];
      marginVal = marginMultiplier.absolute === 0 ? marginMultiplier.multiplier : marginMultiplier.absolute / raw_board_lot_quantity;
      }
      
    const result = qty * marginVal;
    
    // Add detailed logging for option sell orders
    console.log("OPTION SELL margin calculation:", {
      exchange,
      absolute: marginMultiplier.absolute,
      raw_board_lot_quantity,
      marginVal,
      qty,
      result
    });
    
    return result;
  }
  
  // Handle all other order types with existing logic
  if (instrumentName.startsWith("FUT") && exchange !== "NSE") {
      const marginMultiplier = exchangeConfig["FUTURES"][orderSide][marginType.toLowerCase()];
      marginVal = marginMultiplier.absolute === 0 ? marginMultiplier.multiplier : marginMultiplier.absolute;
    }
  else if (instrumentName.startsWith("OPT") && exchange !== "NSE") {
      const marginMultiplier = exchangeConfig["OPTIONS"][orderSide][marginType.toLowerCase()];
    marginVal = marginMultiplier.absolute === 0 ? marginMultiplier.multiplier : marginMultiplier.absolute / raw_board_lot_quantity;
    }
  else if (instrumentName.startsWith("FUT") && exchange === "NSE") {
      const marginMultiplier = exchangeConfig[marginType?.toLowerCase()];
      marginVal = marginMultiplier.absolute === 0 ? marginMultiplier.multiplier : marginMultiplier.absolute;
    }
  else if (instrumentName.startsWith("OPT") && exchange === "NSE") {
      const marginMultiplier = exchangeConfig[orderSide][marginType.toLowerCase()];
      marginVal = marginMultiplier.multiplier;
  }
  
  // For non-option-sell orders
  return (Number(price) * qty) / marginVal;
};

export const calcMinimumQuantity = ({
  order_config,
  exchange,
  orderSide,
  product,
  price,
  instrumentName = "",
  lotsize = 1,
  isSquareOff = false,
  raw_board_lot_quantity = 1
}) => {
  // If this is a square-off operation, bypass minimum quantity validation
  if (isSquareOff) return 0;
  
  if (!price || price <= 0) return 0;
  
  // SPECIAL CASE: For Option SELL orders - handle all exchanges the same way
  if (orderSide === "SELL" && instrumentName?.startsWith("OPT")) {
    // Always use 500 as the standard minimum order value for all exchanges including MCX
    const standardMinOrderValue = 450;
    
    // For MCX, we need to properly consider the raw_board_lot_quantity
    if (exchange === "MCX") {
      // Get the config to retrieve the absolute value
      const marginType = getMarginType(product);
      const exchangeConfig = order_config[exchange];
      const config = exchangeConfig["OPTIONS"]?.[orderSide]?.[marginType.toLowerCase()];
      
      if (config && config.absolute > 0) {
        // Calculate margin per contract based on absolute/raw_board_lot_quantity
        const marginPerContract = config.absolute / raw_board_lot_quantity;
        // Calculate minimum quantity using standardMinOrderValue (500)
        const minQty = Math.ceil(standardMinOrderValue / marginPerContract);
        
        // console.log("MCX OPTION SELL min quantity calculation:", {
        //   exchange,
        //   standardMinOrderValue,
        //   marginPerContract,
        //   raw_board_lot_quantity,
        //   config_absolute: config.absolute,
        //   result: minQty
        // });
        
        return minQty;
      }
    }
    
    // For other exchanges or if MCX calculation fails, use standard approach
    const standardMarginPerContract = 100;
    const minQty = Math.ceil(standardMinOrderValue / standardMarginPerContract);
    
    console.log("OPTION SELL: Standardized min quantity calculation:", {
      exchange,
      standardMinOrderValue,
      standardMarginPerContract,
      raw_board_lot_quantity,
      result: minQty
    });
    
    return minQty;
  }
  
  const marginType = getMarginType(product);
  const exchangeConfig = order_config[exchange];
  
  if (!exchangeConfig) return 0;
  
  let config = null;
  
  // Get the appropriate configuration
  if (instrumentName?.startsWith("FUT") && exchange !== "NSE") {
    config = exchangeConfig["FUTURES"]?.[orderSide]?.[marginType.toLowerCase()];
  } 
  else if (instrumentName?.startsWith("OPT") && exchange !== "NSE") {
    config = exchangeConfig["OPTIONS"]?.[orderSide]?.[marginType.toLowerCase()];
  }
  else if (instrumentName?.startsWith("FUT") && exchange === "NSE") {
    config = exchangeConfig[marginType?.toLowerCase()];
  }
  else if (instrumentName?.startsWith("OPT") && exchange === "NSE") {
    config = exchangeConfig[orderSide]?.[marginType.toLowerCase()];
  }
  
  if (!config || !config.minimumValue) return 0;
  
  // Calculate the minimum order value for non-option-sell orders
  let minimumOrderValue;
  
  if (config.multiplier > 0) {
    minimumOrderValue = config.minimumValue / config.multiplier;
  } else if (config.absolute > 0) {
    // For MCX option sell, always use 500
    if (exchange === "MCX" && orderSide === "SELL" && instrumentName?.startsWith("OPT")) {
      minimumOrderValue = 450;
    } else {
      minimumOrderValue = config.minimumValue / config.absolute;
    }
  } else {
    return 0;
  }
  
  // Standard margin calculation for non-option-sell orders
  const marginPerUnit = price / (config.multiplier > 0 ? config.multiplier : 1);
  
  // Calculate minimum quantity based on minimum order value
  let minQty = Math.ceil(minimumOrderValue / marginPerUnit);
  
  // Adjust to be a multiple of lot size
  if (exchange !== "MCX" && lotsize > 1) {
    minQty = Math.ceil(minQty / lotsize) * lotsize;
  }
  
  return minQty > 0 ? minQty : 1;
};

// Function to get the minimum order value for display
export const getMinimumOrderValue = ({
  order_config,
  exchange,
  orderSide,
  product,
  instrumentName = "",
  raw_board_lot_quantity = 1
}) => {
  // Special case for ALL option sell orders - always return 500
  if (orderSide === "SELL" && instrumentName?.startsWith("OPT")) {
    console.log("OPTION SELL: Returning standard minimum order value 500 for", exchange);
    return 450;
  }

  const marginType = getMarginType(product);
  const exchangeConfig = order_config[exchange];
  
  if (!exchangeConfig) return 0;
  
  let config = null;
  
  // Get the appropriate configuration
  if (instrumentName?.startsWith("FUT") && exchange !== "NSE") {
    config = exchangeConfig["FUTURES"]?.[orderSide]?.[marginType.toLowerCase()];
  } 
  else if (instrumentName?.startsWith("OPT") && exchange !== "NSE") {
    config = exchangeConfig["OPTIONS"]?.[orderSide]?.[marginType.toLowerCase()];
  }
  else if (instrumentName?.startsWith("FUT") && exchange === "NSE") {
    config = exchangeConfig[marginType?.toLowerCase()];
  }
  else if (instrumentName?.startsWith("OPT") && exchange === "NSE") {
    config = exchangeConfig[orderSide]?.[marginType.toLowerCase()];
  }
  
  if (!config || !config.minimumValue) return 0;
  
  // Calculate the minimum order value for other order types
  if (config.multiplier > 0) {
    return config.minimumValue / config.multiplier;
  } else if (config.absolute > 0) {
    return 450;
  } else {
    return 0;
  }
};

export const useOrderForm = ({
  type,
  ltp,
  exchange,
  token,
  channel,
  lotsize,
  tick_size,
  execution,
  bestBid,
  bestAsk,
  payload,
  alternate_exchange,
  onClose,
  trading_symbol,
  onTypeChange,
  is_modify,
  oms_order_id,
  exchange_order_id,
  open,
  strike_price
}) => {
  console.log("payload",payload);
  const dispatch = useDispatch();
  const { userData, isLoaded } = useSelector((state) => state.profile);
  useEffect(() => {
    if (!isLoaded) {
      dispatch(fetchProfileDataAction());
    }
  }, [dispatch, isLoaded]);
  const { userSettings } = useSelector((state) => state.profile);

  const [orderType, setOrderType] = useState(payload?.order_type);
  const [quantity, setQuantity] = useState("1");
  const [price, setPrice] = useState(payload?.price || ltp || 0);
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [hideMargin, setHideMargin] = useState(false);
  const [currentLTP, setCurrentLTP] = useState(ltp);
  const [initialLTP, setInitialLTP] = useState(ltp);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [originalTriggerPrice, setOriginalTriggerPrice] = useState(null);
  const [originalOrderType, setOriginalOrderType] = useState(null);
  const [originalProduct, setOriginalProduct] = useState(null);
  const [currentBestBid, setCurrentBestBid] = useState(bestBid);
  const [currentBestAsk, setCurrentBestAsk] = useState(bestAsk);
  const [product, setProduct] = useState("");
  const [validity, setValidity] = useState("DAY");
  const [executionType, setExecutionType] = useState("REGULAR");
  const [lowDpr, setLowDpr] = useState(0);
  const [highDpr, setHighDpr] = useState(0);
  const [priceError, setPriceError] = useState(false);
  const [triggerError, setTriggerError] = useState(false);
  const [quantityError, setQuantityError] = useState(false);
  const [orderSide, setOrderSide] = useState(type);
  const [orderDetails, setOrderDetails] = useState({
    trigger_price: ltp,
    disclosed_qty: 0,
  });
  const isDisabled = priceError || triggerError || quantityError;

  // Add state for minimum quantity required and minimum order value
  const [minimumQtyRequired, setMinimumQtyRequired] = useState(0);
  const [minimumOrderValue, setMinimumOrderValue] = useState(0);
  
  // Add a state variable to track if the order is a square-off order
  const [isSquareOffOrder, setIsSquareOffOrder] = useState(false);
  const [existingPositionQty, setExistingPositionQty] = useState(0);
  
  // Calculate minimum quantity when relevant inputs change
  useEffect(() => {
    if (product && orderSide && exchange && payload?.instrument_name) {
      // For market orders, use the best bid/ask price instead of the order price
      let priceToUse = parseFloat(price || 0);
      
      if (orderType === "MARKET" || orderType === "STOPLOSSMARKET") {
        priceToUse = orderSide === "BUY" ? 
          parseFloat(currentBestAsk || 0) : 
          parseFloat(currentBestBid || 0);
      }
      
      if (priceToUse <= 0 && currentLTP) {
        // Fallback to LTP if best bid/ask not available
        priceToUse = parseFloat(currentLTP);
      }
      
      // Reset square-off state
      setIsSquareOffOrder(false);
      setExistingPositionQty(0);
      
      // Get the isSquareOff flag from payload
      const isDirectSquareOff = payload?.isSquareOff || false;
      
      // Only calculate if we have a valid price
      if (priceToUse > 0) {
        // Log all relevant variables for option sell orders
        if (orderSide === "SELL" && payload?.instrument_name?.startsWith("OPT")) {
          console.log("OPTION SELL ORDER DETECTED - Input variables:", {
            exchange,
            priceToUse,
            isDirectSquareOff,
            raw_board_lot_quantity: payload?.raw_board_lot_quantity,
            instrumentName: payload?.instrument_name,
            orderSide,
            product
          });
          
          // Special logging for MCX exchange
          if (exchange === "MCX") {
            const marginType = getMarginType(product);
            const exchangeConfig = PRIME_SETTINGS[exchange];
            const config = exchangeConfig?.["OPTIONS"]?.[orderSide]?.[marginType.toLowerCase()];
            
            // console.log("MCX OPTION SELL configuration:", {
            //   marginType,
            //   config,
            //   absolute: config?.absolute,
            //   minimumValue: config?.minimumValue,
            //   raw_board_lot_quantity: payload?.raw_board_lot_quantity
            // });
          }
        }
        
        // Calculate base minimum quantity, passing the square-off flag
        const baseMinQty = calcMinimumQuantity({
          order_config: PRIME_SETTINGS,
          exchange,
          orderSide,
          product,
          price: priceToUse,
          instrumentName: payload?.instrument_name,
          lotsize: lotsize,
          isSquareOff: isDirectSquareOff, // Pass the square-off flag
          raw_board_lot_quantity: payload?.raw_board_lot_quantity || 1
        });

        // Check for matching positions to potentially adjust minimum quantity
        let effectiveMinQty = baseMinQty;
        const positionsArray = window.store?.getState()?.portfolio?.arrPositionsUpdateData || [];
        
        if (positionsArray && positionsArray.length > 0 && !isDirectSquareOff) {
          // Skip position check if it's a direct square-off
          // Find matching position with same token and product type
          const matchingPosition = positionsArray.find(position => 
            (String(position.token) === String(payload?.token) ||
              String(position.instrument_token) === String(payload?.token)) &&
            position.net_quantity !== 0 && 
            position.product === product
          );
          
          if (matchingPosition) {
            const netQuantity = Number(matchingPosition.net_quantity) || 0;
            const positionSide = netQuantity < 0 ? "SELL" : netQuantity > 0 ? "BUY" : null;
            const existingQty = Math.abs(netQuantity);
            
            // Set existing position quantity for validation purposes
            setExistingPositionQty(existingQty);
            
            if (positionSide) {
              // If positions are in the same direction, we already have some quantity
              // For example: if we have a BUY position and placing another BUY order
              if (positionSide === orderSide) {
                // Reduce minimum required quantity by existing position quantity
                effectiveMinQty = Math.max(0, baseMinQty - existingQty);
              }
              // If positions are in opposite direction, it's a square-off
              else if (positionSide !== orderSide) {
                // For square-off positions, we'll bypass minimum quantity validation
                // when the entered quantity is less than or equal to existing position
                setIsSquareOffOrder(true);
                
                // Set effective min qty to zero for square-offs
                effectiveMinQty = 0;
              }
            }
          }
        }
        
        // If it's a direct square-off, minimum quantity should be 0
        setMinimumQtyRequired(isDirectSquareOff ? 0 : effectiveMinQty);
        
        // Calculate minimum order value for display
        const minOrderValue = isDirectSquareOff ? 0 : getMinimumOrderValue({
          order_config: PRIME_SETTINGS,
          exchange,
          orderSide,
          product,
          instrumentName: payload?.instrument_name,
          raw_board_lot_quantity: payload?.raw_board_lot_quantity || 1
        });
        
        setMinimumOrderValue(minOrderValue);
      }
    }
  }, [product, price, orderType, orderSide, exchange, payload?.instrument_name, payload?.token, lotsize, currentBestAsk, currentBestBid, currentLTP, payload?.raw_board_lot_quantity]);

  // Update the quantity error handling to bypass validation for ALL square-off operations
  useEffect(() => {
    const numericQty = parseInt(quantity) || 0;
    const isDirectSquareOff = payload?.isSquareOff || false;
    
    // FIRST CHECK: Direct square-off from payload flag
    if (isDirectSquareOff) {
      console.log("Direct square-off detected - bypassing quantity validation");
      setQuantityError(false);
      return;
    }
    
    // SECOND CHECK: Square-off detected from position matching 
    if (isSquareOffOrder && numericQty <= existingPositionQty) {
      console.log("Position-based square-off detected - bypassing quantity validation for", numericQty, "vs", existingPositionQty);
      setQuantityError(false);
      return;
    }
    
    // THIRD CHECK: Square-off with quantity below or equal to existing position
    // Check if there's a matching position with opposite direction 
    const positionsArray = window.store?.getState()?.portfolio?.arrPositionsUpdateData || [];
    if (positionsArray && positionsArray.length > 0) {
      const matchingPosition = positionsArray.find(position => 
        (String(position.token) === String(payload?.token) ||
         String(position.instrument_token) === String(payload?.token)) &&
        position.net_quantity !== 0 && 
        position.product === product
      );
      
      if (matchingPosition) {
        const netQuantity = Number(matchingPosition.net_quantity) || 0;
        const positionSide = netQuantity < 0 ? "SELL" : "BUY";
        
        // If this is a square-off order (opposite direction of position)
        if ((positionSide === "BUY" && orderSide === "SELL") || 
            (positionSide === "SELL" && orderSide === "BUY")) {
          
          // If entered quantity is less than or equal to position quantity, it's a valid square-off
          if (numericQty <= Math.abs(netQuantity)) {
            console.log("Valid square-off quantity detected:", numericQty, "vs position", Math.abs(netQuantity));
            setQuantityError(false);
            return;
          }
        }
      }
    }
    
    // If not a square-off operation, apply normal validation
    const numericLotSize = parseFloat(lotsize);
    const exchange = payload?.exchange;

    if (!isNaN(numericQty) && numericLotSize > 0) {
      let isValid = numericQty > 0;

      if (exchange !== "MCX" && isValid) {
        isValid = isValidMultiple(numericQty, numericLotSize);
      }

      setQuantityError(
        numericQty === 0 ||
        numericQty === null ||
        (exchange !== "MCX" && !isValidMultiple(numericQty, numericLotSize))
      );
    } else {
      setQuantityError(true);
    }
  }, [quantity, lotsize, payload?.exchange, payload?.token, orderSide, product, isSquareOffOrder, existingPositionQty, payload?.isSquareOff]);

  useEffect(() => {
    if (open) {
      console.log("OrderWindow opened with payload:", payload);
      
      // Check for square-off operation
      const isSquareOffOperation = payload?.isSquareOff || false;
      if (isSquareOffOperation) {
        console.log("Square-off operation detected during initialization");
        // For square-off operations, ensure quantityError is false
        setQuantityError(false);
        setMinimumQtyRequired(0);
      }
      
      setInitialLTP(ltp);
      setProduct(payload.product);
      setOrderType(payload?.order_type || "LIMIT");
      setQuantity(
        !payload.diplayNetQuantity ? "1" : payload?.lot_size || payload?.lotsize
      );
      setHideMargin(payload.diplayNetQuantity);
      
      // Don't set quantityError for square-off operations
      if (!isSquareOffOperation) {
        setQuantityError(false);
      }

      if (is_modify) {
        // Rest of the modify code remains unchanged
        const modifyPrice = payload?.ltp || 0;
        setOriginalPrice(modifyPrice);
        setOriginalTriggerPrice(payload?.trigger_price || 0);
        setOriginalOrderType(payload?.order_type);
        setOriginalProduct(payload?.product);
        setProduct(payload?.product);
        setPrice(orderType === "STOPLOSSMARKET" ? 0 : payload.price);
        setQuantity(payload?.quantity);

        const triggerPrice =
          orderType === "STOPLOSSMARKET" || orderType === "SL"
            ? payload?.trigger_price || modifyPrice || 0
            : 0;

        setOrderDetails((prev) => ({
          ...prev,
          trigger_price: triggerPrice,
          disclosed_qty: payload?.disclosed_qty || 0,
        }));
      } else {
        setOriginalPrice(null);
        setOriginalTriggerPrice(null);
        setOriginalOrderType(null);
        setOriginalProduct(null);
        setPrice(orderType === "STOPLOSSMARKET" ? 0 : ltp);
        setCurrentLTP(ltp);

        setOrderDetails((prev) => ({
          ...prev,
          trigger_price: 0,
          disclosed_qty: 0,
        }));
      }
    }
  }, [open, payload, is_modify, ltp, exchange]);

  useEffect(() => {
    if (orderType === "MARKET") {
      setPrice(0);
      setOrderDetails((prev) => ({ ...prev, trigger_price: 0 }));
    } else if (orderType === "STOPLOSSMARKET") {
      setPrice(0);
      if (is_modify) {
        const preservedTriggerPrice =
          payload?.trigger_price ||
          originalTriggerPrice ||
          (payload?.price && parseFloat(payload.price) > 0
            ? payload.price
            : initialLTP);

        setOrderDetails((prev) => ({
          ...prev,
          trigger_price: preservedTriggerPrice,
        }));
      } else {
        setOrderDetails((prev) => ({
          ...prev,
          trigger_price: initialLTP,
        }));
      }
    } else if (orderType === "SL") {
      const preservedPrice = payload?.price || initialLTP;
      const preservedTriggerPrice =
        payload?.trigger_price ||
        originalTriggerPrice ||
        preservedPrice ||
        initialLTP;

      setPrice(preservedPrice);
      setOrderDetails((prev) => ({
        ...prev,
        trigger_price: preservedTriggerPrice,
      }));
    } else if (orderType === "LIMIT") {
      setPrice(payload?.price || initialLTP);
      setOrderDetails((prev) => ({
        ...prev,
        trigger_price: 0,
      }));
    }
  }, [
    orderType,
    initialLTP,
    is_modify,
    payload,
    originalPrice,
    originalTriggerPrice,
  ]);

  useEffect(() => {
    if (!open || !exchange || !token) return;
    const subscriptionLocation = `buy-sell-modal`;
    const handler = octopusInstance.wsHandler({
      messageType:
        execution === "SPREAD"
          ? "SpreadMarketUpdate"
          : "CompactMarketDataMessage",
      subscriptionLocation,
      payload: {
        exchangeCode: getExchangeCode(exchange),
        instrumentToken: token ? token : null,
      },
    });

    handler.subscribe(({ msg }) => {
      setCurrentBestBid(msg.bidPrice);
      setCurrentBestAsk(msg.askPrice);
      setLowDpr(msg.lowDpr);
      setHighDpr(msg.highDpr);
    });

    return handler.unsubscribe;
  }, [exchange, token, open]);

  useEffect(() => {
    setOrderSide(type);
  }, [type]);

  const getProductOptions = () => {
    if (exchange === "NFO" || exchange === "MCX" || exchange === "CDS") {
      return ["MIS", "NRML"];
    } else if (exchange === "NSE" && payload?.series?.toLowerCase() !== "indices") {
      return ["MIS", "CNC"];
    } else if (exchange === "NSE" && payload?.series?.toLowerCase() === "indices") {
      return ["MIS", "NRML"];
    }
    return ["MIS", "NRML"];
  };


  const hasMatchingPosition = (positionsArray, searchToken, orderSide, quantity, product) => {
    if (!positionsArray?.length || !searchToken || !orderSide) {
      return {
        hideMargin: false,
        netQuantity: Math.abs(quantity)
      }
    }

    const matchingPosition = positionsArray.find(position =>
      (String(position.token) === String(searchToken) ||
        String(position.instrument_token) === String(searchToken)) &&
      position.net_quantity !== 0 && position.product === product
    );

    if (!matchingPosition) {
      return {
        hideMargin: false,
        netQuantity: Math.abs(quantity)
      }
    }

    const netQuantity = Number(matchingPosition.net_quantity) || 0;
    const positionSide = netQuantity < 0 ? "SELL" : netQuantity > 0 ? "BUY" : "No Position";// For SELL order, we need positive quantity

    if (orderSide === "SELL" && positionSide === "BUY") {
      if (Number(quantity) <= Math.abs(netQuantity)) {
        return {
          hideMargin: true,
          netQuantity: 0
        };
      }
      else {
        return {
          hideMargin: false,
          netQuantity: Math.abs(quantity) - Math.abs(netQuantity)
        }
      }
    }
    if (orderSide === "SELL" && positionSide === "SELL") {

      return {
        hideMargin: false,
        netQuantity: quantity
      }
    }
    // For BUY order, we need negative quantity
    if (orderSide === "BUY" && positionSide === "SELL") {
      if (Number(quantity) <= Math.abs(netQuantity)) {
        return {
          hideMargin: true,
          netQuantity: 0
        };
      }
      else {
        return {
          hideMargin: false,
          netQuantity: Math.abs(quantity) - Math.abs(netQuantity)
        }
      }
    }
    if (orderSide === "BUY" && positionSide === "BUY") {
      return {
        hideMargin: false,
        netQuantity: quantity
      }
    }

  };


  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setOrderSide(newType);
      if (onTypeChange) {
        onTypeChange(newType);
      }
    }
  };

  const handleChange = (field) => (e) => {
    setOrderDetails((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const tolerance = 1e-10;

  const isValidMultiple = (value, tickSize) => {
    const remainder = Math.abs(value % tickSize);
    return remainder < tolerance || Math.abs(remainder - tickSize) < tolerance;
  };

  const handlePriceChange = (e) => {
    const inputValue = parseFloat(e.target.value);
    const numericTickSize = parseFloat(tick_size);
    if (!isNaN(inputValue) && numericTickSize > 0) {
      const isValid = isValidMultiple(inputValue, numericTickSize);
      setPriceError(!isValid);
      setPrice(inputValue);
    } else {
      setPrice("");
      setPriceError(false);
    }
  };
  const handleTriggerChange = (e) => {
    const inputValue = parseFloat(e.target.value);
    const numericTickSize = parseFloat(tick_size);
    if (!isNaN(inputValue) && numericTickSize > 0) {
      const isValid = isValidMultiple(inputValue, numericTickSize);
      setTriggerError(!isValid);
      setOrderDetails((prev) => ({
        ...prev,
        trigger_price: inputValue,
      }));
    } else {
      setOrderDetails((prev) => ({
        ...prev,
        trigger_price: "",
      }));
      setTriggerError(false);
    }
  };

  const handleQuantityChange = (e) => {
    const inputValue = parseFloat(e.target.value);
    updateQuantity(inputValue);
  };

  const handleQuantityKeyDown = (e) => {
    const { exchange } = payload;
    let currentQty = parseFloat(quantity) || 0;

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (exchange === "MCX") {
        updateQuantity(currentQty + 1);
      } else {
        updateQuantity(currentQty + (lotsize || 1));
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      let newQty;
      if (exchange === "MCX") {
        newQty = Math.max(0, currentQty - 1);
      } else {
        newQty = Math.max(0, currentQty - (lotsize || 1));
      }
      updateQuantity(newQty);
    }
  };

  const updateQuantity = (value) => {
    const numericLotSize = parseFloat(lotsize);
    const exchange = payload?.exchange;

    if (!isNaN(value) && numericLotSize > 0) {
      let isValid = value > 0;

      if (exchange !== "MCX" && isValid) {
        isValid = isValidMultiple(value, numericLotSize);
      }

      setQuantityError(
        value === 0 ||
        value === null ||
        (exchange !== "MCX" && !isValidMultiple(value, numericLotSize))
      );
      setQuantity(value.toString());
    } else {
      setQuantity("");
      setQuantityError(true);
    }
  };

  const handlePriceKeyDown = (e) => {
    if (orderType === "MARKET") return;

    if (e.key === "ArrowUp") {
      e.preventDefault();
      const currentPrice = parseFloat(price) || 0;
      setPrice((currentPrice + (tick_size || 0.05)).toFixed(2));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const currentPrice = parseFloat(price) || 0;
      setPrice((currentPrice - (tick_size || 0.05)).toFixed(2));
    }
  };
  const handleTriggerKeyDown = (e) => {
    if (orderType === "MARKET") return;

    if (e.key === "ArrowUp") {
      e.preventDefault();
      const currentPrice = parseFloat(orderDetails.trigger_price) || 0; // Use trigger_price from orderDetails
      const updatedPrice = (currentPrice + (tick_size || 0.05)).toFixed(2);

      setOrderDetails((prevDetails) => ({
        ...prevDetails,
        trigger_price: updatedPrice, // Update trigger_price
      }));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const currentPrice = parseFloat(orderDetails.trigger_price) || 0; // Use trigger_price from orderDetails
      const updatedPrice = (currentPrice - (tick_size || 0.05)).toFixed(2);

      setOrderDetails((prevDetails) => ({
        ...prevDetails,
        trigger_price: updatedPrice, // Update trigger_price
      }));
    }
  };

  const handleSubmit = async () => {
    // Get direct square-off flag from payload
    const isDirectSquareOff = payload?.isSquareOff || false;
    console.log("payload", payload);
    // Check if this is a position-based square-off
    let isPositionBasedSquareOff = false;
    
    // Only check if not already a direct square-off
    if (!isDirectSquareOff) {
      const positionsArray = window.store?.getState()?.portfolio?.arrPositionsUpdateData || [];
      if (positionsArray && positionsArray.length > 0) {
        const matchingPosition = positionsArray.find(position => 
          (String(position.token) === String(payload?.token) ||
           String(position.instrument_token) === String(payload?.token)) &&
          position.net_quantity !== 0 && 
          position.product === product
        );
        
        if (matchingPosition) {
          const netQuantity = Number(matchingPosition.net_quantity) || 0;
          const positionSide = netQuantity < 0 ? "SELL" : "BUY";
          const numericQty = parseInt(quantity) || 0;
          
          // If this is a square-off order (opposite direction of position)
          if ((positionSide === "BUY" && orderSide === "SELL") || 
              (positionSide === "SELL" && orderSide === "BUY")) {
            
            // If entered quantity is less than or equal to position quantity, it's a valid square-off
            if (numericQty <= Math.abs(netQuantity)) {
              isPositionBasedSquareOff = true;
              console.log("Valid position-based square-off detected for order submission");
            }
          }
        }
      }
    }
    
    // Final square-off flag (either direct or position-based)
    const isSquareOff = isDirectSquareOff || isPositionBasedSquareOff;
    
    console.log("Submit button clicked:", {
      isDirectSquareOff,
      isPositionBasedSquareOff,
      isSquareOff,
      quantity,
      orderType,
      price,
      quantityError,
      minimumQtyRequired
    });
    
    // Only validate fields if NOT a square-off operation
    if (!isSquareOff && (!quantity || (orderType === "LIMIT" && !price))) {
      showNotification("ERROR", { message: "Please fill all required fields" });
      return;
    }

    if (!isSquareOff && orderType === "STOPLOSSMARKET" && !orderDetails.trigger_price) {
      showNotification("ERROR", { message: "Please fill trigger price field" });
      return;
    }

    setLoading(true);
    try {
      // Get exchange with multiple fallbacks
      let exchangeValue = null;
      
      // First try to get from payload.series and alternate_exchange
      if (payload?.series?.toLowerCase() === "indices") {
        exchangeValue = alternate_exchange;
      }
      // Then try payload.exchange
      else if (payload?.exchange) {
        exchangeValue = payload.exchange;
      }
      // Then try the exchange prop
      else if (exchange) {
        exchangeValue = exchange;
      }

      if (!exchangeValue) {
        console.error('Exchange value not found in any source:', {
          payload,
          exchange,
          series: payload?.series,
          alternate_exchange: alternate_exchange
        });
        throw new Error('Exchange value is missing. Please try again.');
      }

      const orderData = {
        instrument_token: payload?.token || payload?.instrument_token,
        order_side: orderSide,
        order_type:
          orderType === "STOPLOSS"
            ? "SL"
            : orderType === "STOPLOSSMARKET"
              ? "SLM"
              : orderType,
        price:
          orderType === "MARKET"
            ? 0
            : orderType === "STOPLOSSMARKET"
              ? 0
              : parseFloat(price),
        product: product,
        quantity: parseInt(quantity),
        trigger_price:
          orderType === "MARKET"
            ? 0
            : orderType === "LIMIT"
              ? 0
              : orderType === "SL"
                ? price
                : 0,
        disclosed_quantity: 0,
        validity: validity,
        exchange: exchangeValue,
        execution_type: executionType || "REGULAR",
        user_order_id: Date.now(),
        is_square_off: isSquareOff, // Use combined flag
      };

      console.log('Order data being sent:', orderData);

      const response = await apiService.placeOrder(orderData);

      if (response.status === "success") {
        showNotification("NEW_ORDER", {
          order_side: orderSide,
          quantity: quantity,
          symbol: trading_symbol,
          price: orderType === "MARKET" ? "MARKET" : price,
        });
      } else {
        showNotification("ERROR", {
          message: response.message || "Order placement failed",
        });
      }
    } catch (err) {
      console.error("Order placement error:", err);
      showNotification("ERROR", {
        message: err.message || "Failed to place order. Please try again.",
      });
    } finally {
      setLoading(false);
      dispatch(closeOrderWindow());
    }
  };

  const handleModify = async () => {
    // Get direct square-off flag from payload
    const isDirectSquareOff = payload?.isSquareOff || false;
    
    // Check if this is a position-based square-off
    let isPositionBasedSquareOff = false;
    
    // Only check if not already a direct square-off
    if (!isDirectSquareOff) {
      const positionsArray = window.store?.getState()?.portfolio?.arrPositionsUpdateData || [];
      if (positionsArray && positionsArray.length > 0) {
        const matchingPosition = positionsArray.find(position => 
          (String(position.token) === String(payload?.token) ||
           String(position.instrument_token) === String(payload?.token)) &&
          position.net_quantity !== 0 && 
          position.product === product
        );
        
        if (matchingPosition) {
          const netQuantity = Number(matchingPosition.net_quantity) || 0;
          const positionSide = netQuantity < 0 ? "SELL" : "BUY";
          const numericQty = parseInt(quantity) || 0;
          
          // If this is a square-off order (opposite direction of position)
          if ((positionSide === "BUY" && orderSide === "SELL") || 
              (positionSide === "SELL" && orderSide === "BUY")) {
            
            // If entered quantity is less than or equal to position quantity, it's a valid square-off
            if (numericQty <= Math.abs(netQuantity)) {
              isPositionBasedSquareOff = true;
              console.log("Valid position-based square-off detected for order modification");
            }
          }
        }
      }
    }
    
    // Final square-off flag (either direct or position-based)
    const isSquareOff = isDirectSquareOff || isPositionBasedSquareOff;
    
    // Only validate fields if NOT a square-off operation
    if (!isSquareOff && (!quantity || (orderType === "LIMIT" && !price))) {
      showNotification("ERROR", { message: "Please fill all required fields" });
      return;
    }
    
    setLoading(true);
    try {
      const orderData = {
        instrument_token: payload?.token
          ? payload.token
          : payload.instrument_token,
        order_side: orderSide,
        order_type: orderType === "STOPLOSSMARKET" ? "SLM" : orderType,
        price: orderType === "MARKET" ? 0 : parseFloat(price),
        product: product,
        quantity: parseInt(quantity),
        trigger_price: orderType === "MARKET" ? 0 : orderType === "LIMIT" ? 0 : orderType === "SL" ? price : 0,
        disclosed_quantity: 0,
        validity: "DAY",
        exchange: exchange,
        execution_type: execution || "REGULAR",
        user_order_id: Date.now(),
        oms_order_id: oms_order_id,
        exchange_order_id: exchange_order_id,
        is_square_off: isSquareOff, // Use combined flag
      };

      console.log('Modify order data being sent:', orderData);

      const response = await apiService.modifyOrder(orderData);

      if (response.status === "success") {
        // debugger
        showNotification("NEW_ORDER", {
          order_side: orderSide,
          quantity: quantity,
          symbol: trading_symbol,
          price: orderType === "MARKET" ? "MARKET" : price,
        });
        setTimeout(() => {
          onClose();
        }, 1000);
      }
      else{
        enqueueSnackbar(response.message, { variant: "error",anchorOrigin:{vertical:"top",horizontal:"right"} });
      }
    } catch (err) {
      console.log("Order placement error:", err);
      enqueueSnackbar(err.message, { variant: "error" });
      console.error("Order placement error:", err);
    } finally {
      setLoading(false);
      dispatch(fetchPositionsAction());
    }
  };

  const radioTypeOptions = [
    { value: "LIMIT", label: "Lim" },
    { value: "MARKET", label: "Mkt" },
    { value: "SL", label: "SL" },

  ];

  // Add this useEffect at the end of the hook to ensure square-off operations always have minimumQtyRequired set to 0
  useEffect(() => {
    // Force minimumQtyRequired to 0 for square-off operations
    if (payload?.isSquareOff) {
      console.log("SQUARE OFF MODE DETECTED - Forcing minimum quantity to 0");
      console.log("Previous minimumQtyRequired:", minimumQtyRequired);
      console.log("Previous quantityError:", quantityError);
      
      setMinimumQtyRequired(0);
      setQuantityError(false);
      
      // Log again after state updates to confirm changes
      setTimeout(() => {
        console.log("Updated minimumQtyRequired:", minimumQtyRequired);
        console.log("Updated quantityError:", quantityError);
      }, 100);
    }
  }, [payload?.isSquareOff, minimumQtyRequired, quantityError]);

  // Add a useEffect that runs once on mount to detect square-off mode
  useEffect(() => {
    // Check for square-off mode when component mounts
    const isSquareOff = payload?.isSquareOff || false;
    if (isSquareOff) {
      console.log("COMPONENT MOUNT: Square-off mode detected");
      // Force disable validation for square-off
      setMinimumQtyRequired(0);
      setQuantityError(false);
    }
  }, []); // Empty dependency array means this runs once on mount

  return {
    // State
    orderType,
    hideMargin,
    is_modify,
    quantity,
    price,
    loading,
    currentLTP,
    lotsize,
    currentBestBid,
    currentBestAsk,
    product,
    validity,
    executionType,
    priceError,
    triggerError,
    quantityError,
    orderSide,
    orderDetails,
    isDisabled: payload?.isSquareOff ? (priceError || triggerError) : (priceError || triggerError || quantityError), // Override isDisabled for square-off
    userData,
    strike_price,
    minimumQtyRequired,
    minimumOrderValue,
    PRIME_SETTINGS,
    isSquareOffOrder,
    existingPositionQty,
    // Setters
    setOrderType,
    setProduct,
    setPrice,
    setValidity,
    setExecutionType,

    // Handlers
    handleTypeChange,
    handleChange,
    handlePriceChange,
    handleTriggerKeyDown,
    handleTriggerChange,
    handleQuantityChange,
    handleQuantityKeyDown,
    handlePriceKeyDown,
    handleSubmit,
    handleModify,

    // Utils
    getProductOptions,
    radioTypeOptions,
    hasMatchingPosition,
  };
};
